import { useEffect } from "react";
import { Switch, Route, Router as WouterRouter, useLocation, useParams } from "wouter";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useDemoStore } from "@/store/useDemoStore";
import { buildSeed } from "@/data/seed";
import { sessionsSeed } from "@/data/index";

import LandingPage from "@/pages/landing";
import LoginPage from "@/pages/login";
import NotFound from "@/pages/not-found";
import ComingSoon from "@/pages/coming-soon";
import { AuthGate } from "@/components/AuthGate";

import AdminDashboard from "@/pages/admin/dashboard";
import StandardsList from "@/pages/admin/standards-list";
import CreateStandard from "@/pages/admin/create-standard";
import StandardDetail from "@/pages/admin/standard-detail";
import CriteriaConfig from "@/pages/admin/criteria-config";
import ElementsTab from "@/pages/admin/elements-tab";
import DictaminationRules from "@/pages/admin/dictamination-rules";
import PublicationReview from "@/pages/admin/publication-review";
import LmsEditorPage from "@/pages/admin/lms-editor";
import GroupsCalendar from "@/pages/admin/groups-calendar";
import GroupDetail from "@/pages/admin/group-detail";
import AdminUsersPage from "@/pages/admin/users";
import AdminCandidatesPage from "@/pages/admin/candidates";
import RutasFormativasAdmin from "@/pages/admin/rutas-formativas";

import CandidateDashboard from "@/pages/alumno/dashboard";
import DiagnosticEvaluation from "@/pages/alumno/diagnostic";
import TheoreticalExam from "@/pages/alumno/theoretical-exam";
import CandidateExpediente from "@/pages/alumno/expediente";
import CandidateEvidencias from "@/pages/alumno/evidencias";
import CandidateHistory from "@/pages/alumno/history";
import CandidateRuta from "@/pages/alumno/ruta";
import CandidateLesson from "@/pages/alumno/lesson";
import PreopSimulator from "@/pages/alumno/preop-simulator";

import EvaluatorDashboard from "@/pages/evaluador/dashboard";
import Prechecklist from "@/pages/evaluador/prechecklist";
import PracticalEvaluation from "@/pages/evaluador/practical-evaluation";
import EvaluatorHistory from "@/pages/evaluador/history";

import DictaminadorBandeja from "@/pages/dictaminador/bandeja";
import Dictamination from "@/pages/dictaminador/dictamination";
import Certificate from "@/pages/dictaminador/certificate";
import DictaminadorHistorial from "@/pages/dictaminador/history";

import AuditorDashboard from "@/pages/auditor/dashboard";
import AuditorExpediente from "@/pages/auditor/expediente";
import AuditorBitacora from "@/pages/auditor/bitacora";

import InstructorDashboard from "@/pages/instructor/dashboard";
import InstructorGroups from "@/pages/instructor/grupos";
import InstructorGroupDetail from "@/pages/instructor/grupo-detail";
import InstructorMaterial from "@/pages/instructor/material";
import InstructorMateriales from "@/pages/instructor/materiales";
import InstructorPracticas from "@/pages/instructor/practicas";

import AdminIa from "@/pages/admin/ia";
import AlumnoIa from "@/pages/alumno/ia";
import InstructorIa from "@/pages/instructor/ia";
import EvaluadorIa from "@/pages/evaluador/ia";
import DictaminadorIa from "@/pages/dictaminador/ia";
import AuditorIa from "@/pages/auditor/ia";

const queryClient = new QueryClient();

const placeholderRoutes: { path: string; title: string; module: string; description?: string }[] = [
  { path: "/admin/reglas", title: "Reglas de dictamen", module: "Admin · Reglas", description: "Visión global de las reglas configurables para todos los estándares activos." },
  { path: "/admin/instrumentos", title: "Catálogo de instrumentos", module: "Admin · Instrumentos", description: "Inventario centralizado de listas de cotejo, rúbricas y exámenes por estándar." },
  { path: "/admin/calendario", title: "Calendario académico", module: "Admin · Calendario", description: "Programación de generaciones, evaluaciones y entregas por sede." },
  { path: "/admin/evaluaciones", title: "Evaluaciones", module: "Admin · Evaluaciones", description: "Tablero ejecutivo de evaluaciones programadas, en curso y concluidas." },
  { path: "/admin/expedientes", title: "Expedientes", module: "Admin · Expedientes", description: "Repositorio único de expedientes con visor de evidencias y trazabilidad." },
  { path: "/admin/reportes", title: "Reportes", module: "Admin · Reportes", description: "Indicadores institucionales, tableros por estándar y exportación." },
  { path: "/admin/configuracion", title: "Configuración", module: "Admin · Configuración", description: "Preferencias generales, marca, branding y políticas de privacidad." },

  { path: "/alumno/evidencias", title: "Mis evidencias", module: "Candidato · Evidencias", description: "Carga y organización de evidencias documentales, videos y fotos." },

  { path: "/evaluador/evaluaciones", title: "Evaluaciones asignadas", module: "Evaluador · Bandeja", description: "Bandeja de evaluaciones por aplicar con prioridad y SLA." },
  { path: "/evaluador/candidatos", title: "Mis candidatos", module: "Evaluador · Candidatos", description: "Listado de candidatos asignados y su progreso." },
  { path: "/evaluador/instrumentos", title: "Mis instrumentos", module: "Evaluador · Instrumentos", description: "Instrumentos disponibles por estándar para aplicación en sitio." },

  { path: "/dictaminador/expedientes", title: "Expedientes para dictamen", module: "Dictaminador · Expedientes", description: "Búsqueda y filtrado de expedientes para revisión." },
  { path: "/dictaminador/certificados", title: "Certificados emitidos", module: "Dictaminador · Certificados", description: "Listado de certificados emitidos con folios verificables." },
  { path: "/dictaminador/revocaciones", title: "Revocaciones", module: "Dictaminador · Revocaciones", description: "Solicitudes y trámites de revocación de certificación." },

  { path: "/instructor/avance", title: "Avance de candidatos", module: "Instructor · Avance", description: "Tablero de seguimiento individual y por equipo." },

  { path: "/auditor/expedientes", title: "Revisión de expedientes", module: "Auditor · Expedientes", description: "Acceso a expedientes para revisión transversal." },
  { path: "/auditor/integridad", title: "Integridad del proceso", module: "Auditor · Integridad", description: "Comprobaciones automáticas de cadena de evidencias." },
  { path: "/auditor/inconsistencias", title: "Inconsistencias", module: "Auditor · Inconsistencias", description: "Alertas IA y casos a revisión manual." },
  { path: "/auditor/recertificacion", title: "Recertificación", module: "Auditor · Recertificación", description: "Pipeline de recertificaciones por vigencia." },
  { path: "/auditor/reportes", title: "Reportes de auditoría", module: "Auditor · Reportes", description: "Reportes ejecutivos para órganos rectores." },
];

function Router() {
  const [location] = useLocation();
  const isPublicRoute = location === "/" || location === "/login";

  const routes = (
    <Switch>
      <Route path="/" component={LandingPage} />
      <Route path="/login" component={LoginPage} />

      <Route path="/admin" component={AdminDashboard} />
      <Route path="/admin/estandares" component={StandardsList} />
      <Route path="/admin/estandares/nuevo" component={CreateStandard} />
      <Route path="/admin/estandares/:codigo/elementos/:elementId/criterios" component={CriteriaConfig} />
      <Route path="/admin/estandares/:codigo/elementos" component={ElementsTab} />
      <Route path="/admin/estandares/:codigo/instrumentos" component={RedirectToElements} />
      <Route path="/admin/estandares/:codigo/reglas" component={DictaminationRules} />
      <Route path="/admin/estandares/:codigo/publicar" component={PublicationReview} />
      <Route path="/admin/estandares/:codigo/lms" component={LmsEditorPage} />
      <Route path="/admin/estandares/:codigo" component={StandardDetail} />
      <Route path="/admin/grupos" component={GroupsCalendar} />
      <Route path="/admin/grupos/:groupId" component={GroupDetail} />
      <Route path="/admin/candidatos" component={AdminCandidatesPage} />
      <Route path="/admin/usuarios" component={AdminUsersPage} />
      <Route path="/admin/rutas-formativas" component={RutasFormativasAdmin} />

      <Route path="/alumno" component={CandidateDashboard} />
      <Route path="/alumno/diagnostico" component={DiagnosticEvaluation} />
      <Route path="/alumno/examen-teorico" component={TheoreticalExam} />
      <Route path="/alumno/expediente" component={CandidateExpediente} />
      <Route path="/alumno/evidencias" component={CandidateEvidencias} />
      <Route path="/alumno/historial" component={CandidateHistory} />
      <Route path="/alumno/ruta" component={CandidateRuta} />
      <Route path="/alumno/ruta/modulo/:moduloId" component={CandidateLesson} />
      <Route path="/alumno/ruta/simulador/:moduloId" component={PreopSimulator} />

      <Route path="/evaluador" component={EvaluatorDashboard} />
      <Route path="/evaluador/historial" component={EvaluatorHistory} />
      <Route path="/evaluador/evaluacion/:expedienteId/prechecklist" component={Prechecklist} />
      <Route path="/evaluador/evaluacion/:expedienteId" component={PracticalEvaluation} />

      <Route path="/dictaminador" component={DictaminadorBandeja} />
      <Route path="/dictaminador/bandeja" component={DictaminadorBandeja} />
      <Route path="/dictaminador/historial" component={DictaminadorHistorial} />
      <Route path="/dictaminador/expediente/:expedienteId" component={Dictamination} />
      <Route path="/dictaminador/certificado/:expedienteId" component={Certificate} />

      <Route path="/auditor" component={AuditorDashboard} />
      <Route path="/auditor/bitacora" component={AuditorBitacora} />
      <Route path="/auditor/expediente/:expedienteId" component={AuditorExpediente} />

      <Route path="/instructor" component={InstructorDashboard} />
      <Route path="/instructor/grupos" component={InstructorGroups} />
      <Route path="/instructor/grupos/:groupId" component={InstructorGroupDetail} />
      <Route path="/instructor/material" component={InstructorMaterial} />
      <Route path="/instructor/materiales" component={InstructorMateriales} />
      <Route path="/instructor/practicas" component={InstructorPracticas} />

      <Route path="/admin/ia" component={AdminIa} />
      <Route path="/alumno/ia" component={AlumnoIa} />
      <Route path="/instructor/ia" component={InstructorIa} />
      <Route path="/evaluador/ia" component={EvaluadorIa} />
      <Route path="/dictaminador/ia" component={DictaminadorIa} />
      <Route path="/auditor/ia" component={AuditorIa} />

      {placeholderRoutes.map((r) => (
        <Route key={r.path} path={r.path}>
          <ComingSoon title={r.title} module={r.module} description={r.description} />
        </Route>
      ))}

      <Route component={NotFound} />
    </Switch>
  );

  return isPublicRoute ? routes : <AuthGate>{routes}</AuthGate>;
}

function RedirectToElements() {
  const params = useParams<{ codigo: string }>();
  const [, setLocation] = useLocation();
  useEffect(() => {
    setLocation(`/admin/estandares/${params.codigo}/elementos`, { replace: true });
  }, [params.codigo, setLocation]);
  return null;
}

function App() {
  // Repara lmsRouteByStandard si quedó vacía por un merge roto previo.
  // Inyecta sesiones de práctica nuevas (con ejerciciosCubiertos) que no existen en el store del usuario.
  // Ambos efectos corren en cada mount (HMR incluido) sin necesitar hard refresh.
  useEffect(() => {
    const state = useDemoStore.getState();

    // 1. LMS route
    const ec1781 = state.lmsRouteByStandard["EC1781"];
    if (!ec1781 || ec1781.modules.length === 0) {
      useDemoStore.setState({
        lmsRouteByStandard: buildSeed({ includeEc1781: true }).lmsRouteByStandard,
      });
    }

    // 2. Inyectar sesiones nuevas del seed que no existan aún en el store
    const practiceIds = new Set(["ses-g1-5b","ses-g1-p4","ses-g1-p5","ses-g1-p6","ses-g2-p1","ses-g2-p2","ses-g2-p3","ses-g2-p4"]);
    const newByGroup: Record<string, typeof sessionsSeed> = {};
    for (const ses of sessionsSeed) {
      if (!practiceIds.has(ses.id)) continue;
      const existing = state.sessionsByGroup[ses.grupoId] ?? [];
      if (existing.some((s) => s.id === ses.id)) continue;
      if (!newByGroup[ses.grupoId]) newByGroup[ses.grupoId] = [...existing];
      newByGroup[ses.grupoId].push(ses);
    }
    // Also update existing sessions that lack ejerciciosCubiertos
    const updateIds = new Set(["ses-g1-6","ses-g1-7"]);
    for (const ses of sessionsSeed) {
      if (!updateIds.has(ses.id) || !ses.ejerciciosCubiertos) continue;
      const grupoId = ses.grupoId;
      const existing = newByGroup[grupoId] ?? [...(state.sessionsByGroup[grupoId] ?? [])];
      const idx = existing.findIndex((s) => s.id === ses.id);
      if (idx !== -1 && !existing[idx].ejerciciosCubiertos) {
        if (!newByGroup[grupoId]) newByGroup[grupoId] = existing;
        newByGroup[grupoId] = newByGroup[grupoId].map((s) => s.id === ses.id ? { ...s, ...ses } : s);
      }
    }
    if (Object.keys(newByGroup).length > 0) {
      useDemoStore.setState((s) => ({
        sessionsByGroup: { ...s.sessionsByGroup, ...newByGroup },
      }));
    }
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, "")}>
          <Router />
        </WouterRouter>
        <Toaster />
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
